import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { deleteUser, signInWithPopup, GoogleAuthProvider } from 'firebase/auth'

import { auth } from '../../firebase'
import useUtils from '../../hooks/useUtilities'

import { BackgroundMask } from '@/views/Team/workspace/components/ui/TeamDeleteConfirmationDialog'

export default function AccountDeleteConfirmationModal({ open, handleClose }) {
  const { showSnackbar } = useUtils()
  const [loading, setLoading] = useState(false)

  const promptForGoogleReSignIn = async () => {
    setLoading(true)
    try {
      const provider = new GoogleAuthProvider()

      await signInWithPopup(auth, provider)
      deleteUser(auth.currentUser)
        .then(() => {
          setLoading(false)
          showSnackbar('User account deleted successfully', 'success')
        })
        .catch(error => {
          setLoading(false)
          showSnackbar(error?.message, 'error')
        })
    } catch (error) {
      setLoading(false)
      showSnackbar(error?.message, 'error')
    }
  }

  const handleDeleteAccount = () => {
    setLoading(true)
    deleteUser(auth.currentUser)
      .then(() => {
        showSnackbar('User account deleted successfully', 'success')
        setLoading(false)
      })
      .catch(error => {
        setLoading(false)
        if (error.code === `auth/requires-recent-login`) {
          promptForGoogleReSignIn()
        } else {
          showSnackbar(error?.message, 'error')
        }
      })
  }

  return (
    <Dialog
      PaperProps={{ sx: { borderRadius: 4, p: 0, minWidth: 400, maxWidth: 420 } }}
      open={open}
      onClose={handleClose}
    >
      <BackgroundMask icon={DeleteIcon} />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: 3, pb: 0, zIndex: 1 }}>
        <IconButton sx={{ ml: 'auto' }} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent
        sx={{ pt: 2, pb: 0, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', zIndex: 1 }}
      >
        <Typography fontWeight={700} sx={{ mt: 1, mb: 1, fontSize: 20 }} variant="h6">
          Are you sure you want to delete your account?
        </Typography>
        <Typography sx={{ color: '#555', mb: 2, fontSize: 15 }}>
          Deleting your account will erase all your data and credits. If you have an active plan, it won't be refunded
          or transferred.
        </Typography>
        <Typography sx={{ color: '#7A7A7A', fontSize: 15, mb: 1 }}>
          Contact support for help before proceeding.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button
          fullWidth
          color="secondary"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            minWidth: 120,
            height: 44,
            borderColor: '#D1D5DB',
            color: '#222',
            mr: 1.5,
            background: '#fff',
            '&:hover': { background: '#f5f5f5', borderColor: '#BDBDBD' },
          }}
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            minWidth: 160,
            height: 44,
            background: '#E53935',
            '&:hover': { background: '#C62828' },
            boxShadow: 'none',
          }}
          variant="contained"
          onClick={handleDeleteAccount}
        >
          Delete Account
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function DeleteIcon(props) {
  return (
    <svg fill="none" height={48} viewBox="0 0 48 48" width={48} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z"
        fill="#FEE4E2"
      />
      <path
        d="M28 18V17.2C28 16.0799 28 15.5198 27.782 15.092C27.5903 14.7157 27.2843 14.4097 26.908 14.218C26.4802 14 25.9201 14 24.8 14H23.2C22.0799 14 21.5198 14 21.092 14.218C20.7157 14.4097 20.4097 14.7157 20.218 15.092C20 15.5198 20 16.0799 20 17.2V18M22 23.5V28.5M26 23.5V28.5M15 18H33M31 18V29.2C31 30.8802 31 31.7202 30.673 32.362C30.3854 32.9265 29.9265 33.3854 29.362 33.673C28.7202 34 27.8802 34 26.2 34H21.8C20.1198 34 19.2798 34 18.638 33.673C18.0735 33.3854 17.6146 32.9265 17.327 32.362C17 31.7202 17 30.8802 17 29.2V18"
        stroke="#D92D20"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
    </svg>
  )
}
